import { minBy } from 'es-toolkit';

import { AnalogJoint, Component, ComponentDesign, isTruss, Member, Profile } from '@paragon/contract-library';
import { LineSegment2D } from '@paragon/geometry-library';
import { areAnySpinnersActive } from '@paragon/task-spinner';
import {
  calculateBearingGeometry,
  calculateMemberOverallLengthSegment,
  calculateMemberPolygon,
} from '@paragon/three-shared';

import type { AppDispatch, RootState } from '../../store';
import { selectItemByGuid } from '../../store/actions/selectActions';
import { waitForComponentDesignToFinishSaving } from '../../store/middlewares/saveComponentDesignMiddleware';
import { isAsyncRequestRunningForActiveComponentDesign } from '../../store/selectors/blockingOperationSelectors';
import { getActiveComponentDesign } from '../../store/selectors/getActiveComponentDesignSelector';
import { getCommittedComponentDesigns } from '../../store/selectors/getComponentDesignsSelectors';
import { describeProfileGeometry } from './profileGeometry';
import { ToolError } from './types';

const SETTLE_POLL_INTERVAL_MS = 100;
const SETTLE_TIMEOUT_MS = 5 * 60 * 1000;

/**
 * Waits until the app has finished what the last action started: no spinners, no requests running for the open
 * Truss, and the open Truss saved. Tools call this after dispatching, so the next tool sees the saved result.
 */
export async function waitForAppToSettle(getState: () => RootState, timeoutMs = SETTLE_TIMEOUT_MS) {
  const start = Date.now();
  // Only work in flight counts. Modes such as re-webbing or an open insertion also block the designer's next command,
  // but a tool that opened one would otherwise wait on itself.
  const isBusy = () => areAnySpinnersActive(getState()) || isAsyncRequestRunningForActiveComponentDesign(getState());

  while (isBusy()) {
    if (Date.now() - start > timeoutMs) throw new ToolError('The app is still busy. Try again in a moment.');
    await new Promise((resolve) => setTimeout(resolve, SETTLE_POLL_INTERVAL_MS));
  }

  const activeComponentDesignGuid = getState().selections.activeComponentDesignGuid;
  if (activeComponentDesignGuid != null) await waitForComponentDesignToFinishSaving(activeComponentDesignGuid);
}

/**
 * Waits for Re-web to start after `startRewebTruss`. Starting it kicks off a first re-web without waiting for it, so
 * leaving Re-web mode before that finishes breaks the app; this waits until its webs are in.
 */
export async function waitForRewebToStart(getState: () => RootState, timeoutMs = SETTLE_TIMEOUT_MS) {
  const start = Date.now();
  for (;;) {
    const { rewebbingInfo } = getState().application;
    if (rewebbingInfo == null) throw new ToolError('The app could not start re-webbing this truss.');
    if (rewebbingInfo.webbingInfo != null) break;
    if (Date.now() - start > timeoutMs) throw new ToolError('Re-webbing this truss is taking too long.');
    await new Promise((resolve) => setTimeout(resolve, SETTLE_POLL_INTERVAL_MS));
  }
  await waitForAppToSettle(getState, timeoutMs);
}

export function requireProject(state: RootState): string {
  const openContext = state.application.openContext;
  if (openContext?.type !== 'project')
    throw new ToolError('No project is open. Open one with open_project, or start one with create_project.');
  return openContext.projectGuid;
}

function normalizeName(name: string) {
  return name.trim().toLowerCase();
}

export function findTrussByName(state: RootState, name: string): ComponentDesign {
  const componentDesigns = getCommittedComponentDesigns(state).filter(
    (componentDesign) => normalizeName(componentDesign.component.name) === normalizeName(name),
  );

  if (componentDesigns.length === 0) throw new ToolError(`There is no truss named "${name}".`);
  if (componentDesigns.length > 1)
    throw new ToolError(`More than one truss is named "${name}". Ask the designer which one they mean.`);
  return componentDesigns[0];
}

/**
 * Opens the Truss in the truss editor, as clicking it in the navigation panel does, and returns it once it's loaded.
 */
export async function openTruss(dispatch: AppDispatch, getState: () => RootState, name: string) {
  const componentDesign = findTrussByName(getState(), name);
  if (getState().selections.activeComponentDesignGuid !== componentDesign.guid) {
    await dispatch(selectItemByGuid(componentDesign.guid, 'componentDesign', { force: true }));
    await waitForAppToSettle(getState);
  }

  const activeComponentDesign = getActiveComponentDesign(getState());
  if (activeComponentDesign?.guid !== componentDesign.guid) throw new ToolError(`Could not open the truss "${name}".`);
  return activeComponentDesign;
}

export function getTrussMembers(componentDesign: ComponentDesign): readonly Member[] {
  const component = componentDesign.component;
  return 'braceMembers' in component ? component.members.concat(component.braceMembers) : component.members;
}

export function findMemberByName(componentDesign: ComponentDesign, name: string): Member {
  const matches = getTrussMembers(componentDesign).filter(
    (candidate) => normalizeName(candidate.name) === normalizeName(name),
  );
  // A newly generated truss, such as an attic, can number each part of it from W1; Upgrade and Analyze renames them.
  if (matches.length > 1)
    throw new ToolError(
      `The truss "${componentDesign.component.name}" has ${matches.length} members named "${name}". Run ` +
        'upgrade_and_analyze on it to give every member its own name, then read it again with get_truss.',
    );
  const member = matches[0];
  if (member == null)
    throw new ToolError(
      `The truss "${componentDesign.component.name}" has no member named "${name}". Its members are ` +
        `${getTrussMembers(componentDesign)
          .map((candidate) => candidate.name)
          .join(', ')}.`,
    );
  return member;
}

/**
 * Finds a Joint by the name the analysis gives it, accepting "Joint 11" or "11".
 */
export function findJointByName(componentDesign: ComponentDesign, name: string): AnalogJoint {
  const joints = componentDesign.analogModel?.analogJoints;
  if (joints == null) throw new ToolError('This truss has no analysis model yet. Run Upgrade and Analyze first.');

  const jointName = normalizeName(name).replace(/^joint\s*/, '');
  const joint = joints.find((candidate) => normalizeName(candidate.name) === jointName);
  if (joint == null) throw new ToolError(`There is no Joint ${jointName} in this truss.`);
  return joint;
}

/**
 * Every joint in a truss's analysis model, in name order, with where it is, how it's supported, the members that meet
 * there, and the plates on it, so a joint the analysis names can be found without reading each one.
 */
export function describeJoints(componentDesign: ComponentDesign) {
  const model = componentDesign.analogModel;
  if (model == null) throw new ToolError('This truss has no analysis model yet. Run Upgrade and Analyze first.');

  const memberNames = new Map(getTrussMembers(componentDesign).map((member) => [member.guid, member.name]));
  const membersAt = new Map<string, Set<string>>();
  for (const link of model.analogLinks)
    for (const joint of [link.iJoint, link.jJoint])
      for (const guid of link.memberStack.memberGuids) {
        const name = memberNames.get(guid);
        if (name != null)
          (membersAt.get(joint.guid) ?? membersAt.set(joint.guid, new Set()).get(joint.guid)!).add(name);
      }

  // A plate sits over its joint, though a heel plate can sit a few inches up the chord from it: each plate belongs to
  // the joint nearest it.
  const platesAt = new Map<string, string[]>();
  for (const plate of componentDesign.component.platePairs) {
    const distance = (joint: AnalogJoint) => Math.hypot(plate.centerX - joint.x, plate.centerY - joint.y);
    const nearest = minBy(model.analogJoints, distance);
    if (nearest != null && distance(nearest) <= 8)
      (platesAt.get(nearest.guid) ?? platesAt.set(nearest.guid, []).get(nearest.guid)!).push(plate.name);
  }

  return [...model.analogJoints]
    .sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
    .map((joint) => ({
      name: `Joint ${joint.name}`,
      x: Math.round(joint.x * 16) / 16,
      y: Math.round(joint.y * 16) / 16,
      ...(joint.boundaryType !== 'Unrestrained' ? { support: joint.boundaryType } : {}),
      members: [...(membersAt.get(joint.guid) ?? [])].sort(),
      plates: platesAt.get(joint.guid) ?? [],
    }));
}

export function describeLumber(member: Member) {
  const { nominalThickness, nominalWidth, grade, species } = member.lumber;
  return `${nominalThickness}x${nominalWidth} ${species} ${grade}`;
}

/**
 * The summary truss tools return, so the Assistant always has the latest Member names.
 */
export function summarizeTruss(componentDesign: ComponentDesign) {
  const component = componentDesign.component;
  return {
    name: component.name,
    type: component.componentType,
    ...describeDefinition(component),
    span: component.span,
    ...('numberOfPlies' in component ? { plies: component.numberOfPlies, spacing: component.spacing } : {}),
    members: getTrussMembers(componentDesign).map((member) => ({
      name: member.name,
      type: member.memberType,
      lumber: describeLumber(member),
      length: getMemberLength(member, component),
      ...describeMemberGeometry(member, component),
      locked: componentDesign.membersLocked || componentDesign.lockedMembers.includes(member.guid),
    })),
    bearings: component.bearings.map((bearing) => ({
      name: bearing.name,
      ...describeBearingGeometry(bearing, component),
    })),
    splices: component.chordSplices.map((splice) => ({
      name: splice.name,
      at: roundPoint({ x: splice.centerPointX, y: splice.centerPointY }),
    })),
    gussets: ('gussets' in component ? component.gussets : []).map((gusset) => ({
      material: gusset.material,
      outline: gusset.geometry.map(roundPoint),
    })),
    ...(componentDesign.profile == null ? {} : { geometry: describeGeometrySafely(componentDesign.profile) }),
  };
}

function describeGeometrySafely(profile: Profile) {
  try {
    return describeProfileGeometry(profile);
  } catch {
    return null;
  }
}

export const roundToSixteenth = (value: number) => Math.round(value * 16) / 16;

/** What to do instead of a profile-only tool on a truss with no profile, such as a floor truss built from a pattern. */
export const NO_PROFILE_ALTERNATIVE =
  'To move where a web meets a chord, delete it with delete_members and draw it again with add_members, using the ' +
  'centerlines get_truss gives.';

/**
 * Whether a truss is built from a profile, so the tools that edit its profile apply, and which don't when it isn't.
 */
function describeDefinition(component: Component) {
  if (!isTruss(component)) return {};
  if (component.definitionType === 'Profile') return { definition: 'Profile' };
  return {
    definition: component.definitionType,
    notForThisTruss:
      'It has no profile, so move_joint, set_truss_profile, set_webs, reweb_truss, get_web_segments, and ' +
      `reshape_truss's pitch, heel height, and overhangs don't apply. ${NO_PROFILE_ALTERNATIVE}`,
  };
}

/**
 * The jack-girder connections a truss is part of, as a girder or as a jack, read from the app.
 */
export function describeJackConnections(state: RootState, componentDesignGuid: string) {
  const nameOf = (guid: string) => state.componentDesigns.data[guid]?.component.name ?? null;
  return Object.values(state.jackGirderConnections.data)
    .filter(
      (connection) => connection.girderGuid === componentDesignGuid || connection.jackGuid === componentDesignGuid,
    )
    .map((connection) => {
      const jack = state.componentDesigns.data[connection.jackGuid]?.component;
      return {
        girder: nameOf(connection.girderGuid),
        jack: nameOf(connection.jackGuid),
        jackBearing: jack?.bearings.find((bearing) => bearing.guid === connection.jackBearingGuid)?.name ?? null,
        at: roundToSixteenth(connection.xLocation),
        chord: connection.memberType,
        numberOfJacks: connection.numberOfJacks,
        spacingOfJacks: connection.spacingOfJacks,
      };
    });
}
export const roundPoint = (point: { x: number; y: number }) => ({
  x: roundToSixteenth(point.x),
  y: roundToSixteenth(point.y),
});

/**
 * Where a member is, in the truss's coordinates (inches from the left end, up from the bottom), the same ones
 * add_members takes: its centerline from end to end, and its outline's corners as cut.
 */
export function describeMemberGeometry(member: Member, component: Component) {
  try {
    const segment = calculateMemberOverallLengthSegment(member, component);
    const polygon = calculateMemberPolygon(member, component);
    return {
      centerline: segment == null ? null : { from: roundPoint(segment.start), to: roundPoint(segment.end) },
      outline: polygon == null ? null : polygon.vertices.map(roundPoint),
    };
  } catch {
    return { centerline: null, outline: null };
  }
}

export function describeBearingGeometry(bearing: Component['bearings'][number], component: Component) {
  try {
    const geometry = calculateBearingGeometry(bearing, component);
    return geometry == null ? {} : { center: roundPoint(geometry.position), width: roundToSixteenth(geometry.width) };
  } catch {
    return {};
  }
}

/**
 * A member's overall length in inches, rounded to a sixteenth, or `null` if the app can't compute it.
 */
function getMemberLength(member: Member, component: Component) {
  try {
    const segment = calculateMemberOverallLengthSegment(member, component);
    return segment == null ? null : Math.round(LineSegment2D.fromSegmentObject(segment).length * 16) / 16;
  } catch {
    return null;
  }
}

export function requireString(input: Record<string, unknown>, key: string): string {
  const value = input[key];
  if (typeof value !== 'string' || value.trim() === '') throw new ToolError(`"${key}" must be a non-empty string.`);
  return value;
}

export function requireNumber(input: Record<string, unknown>, key: string): number {
  const value = input[key];
  if (typeof value !== 'number' || !Number.isFinite(value)) throw new ToolError(`"${key}" must be a number.`);
  return value;
}

export function optionalNumber(input: Record<string, unknown>, key: string): number | undefined {
  return input[key] === undefined ? undefined : requireNumber(input, key);
}

export function requireStringArray(input: Record<string, unknown>, key: string): string[] {
  const value = input[key];
  if (!Array.isArray(value) || value.some((item) => typeof item !== 'string'))
    throw new ToolError(`"${key}" must be a list of strings.`);
  return value;
}
